// ApiKeyRequiredModal.js - Enhanced version
import React, { useState, useEffect } from 'react';
import FreeTrialManager from './FreeTrialManager';
import Spinner from './Spinner';
import './ApiKeyRequiredModal.css';

const ApiKeyRequiredModal = ({ isOpen, onClose, onOpenSettings }) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);

  // Whenever isOpen changes, check if we should actually show the modal
  useEffect(() => {
    if (!isOpen) {
      setShouldShow(false);
      return;
    }

    // Set checking state to show loading spinner
    setIsChecking(true);
    setFadeOut(false);

    // Double-check if trial is active before showing the modal
    const checkTrialStatus = async () => {
      try {
        console.log("ApiKeyRequiredModal: Double-checking trial status");
        
        // First, check localStorage directly for quick answer
        const storedTrial = localStorage.getItem('freeTrialData');
        if (storedTrial) {
          try {
            const parsedData = JSON.parse(storedTrial);
            if (parsedData.active && parsedData.remaining > 0) {
              console.log("ApiKeyRequiredModal: Trial is active based on localStorage, not showing modal");
              setShouldShow(false);
              setIsChecking(false);
              onClose(); // Auto-close the modal
              return;
            }
          } catch (err) {
            console.error("ApiKeyRequiredModal: Error parsing trial data:", err);
          }
        }
        
        // If localStorage check inconclusive, check with FreeTrialManager
        const isTrialActive = await FreeTrialManager.isTrialActive();
        const remainingPrompts = FreeTrialManager.getRemainingPromptCount();
        
        console.log("ApiKeyRequiredModal: Trial status check:", 
                   { active: isTrialActive, remaining: remainingPrompts });
        
        if (isTrialActive && remainingPrompts > 0) {
          console.log("ApiKeyRequiredModal: Trial is active, not showing modal");
          setShouldShow(false);
          if (onClose) onClose();
        } else {
          console.log("ApiKeyRequiredModal: No active trial found, showing modal");
          setShouldShow(true);
        }
      } catch (err) {
        console.error("ApiKeyRequiredModal: Error checking trial status:", err);
        setShouldShow(true); // Show modal on error to be safe
      } finally {
        setIsChecking(false);
      }
    };
    
    // Add a small delay to ensure FreeTrialManager has completed initialization
    const timer = setTimeout(() => {
      checkTrialStatus();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, [isOpen, onClose]);

  // Listen for trial activation events to auto-close
  useEffect(() => {
    const handleTrialUpdated = () => {
      console.log("ApiKeyRequiredModal: Trial updated event received");
      // Check if trial is now active
      try {
        const isActive = FreeTrialManager.isTrialActive();
        if (isActive) {
          console.log("ApiKeyRequiredModal: Trial became active, closing modal");
          setShouldShow(false);
          if (onClose) onClose();
        }
      } catch (err) {
        console.error("ApiKeyRequiredModal: Error handling trial update:", err);
      }
    };
    
    // Listen for the trialUpdated event
    window.addEventListener('trialUpdated', handleTrialUpdated);
    
    return () => {
      window.removeEventListener('trialUpdated', handleTrialUpdated);
    };
  }, [onClose]);

  // Handle smooth close animation
  const handleClose = () => {
    setFadeOut(true);
    setTimeout(() => {
      if (onClose) onClose();
    }, 200); // Match animation duration
  };

  const handleOpenSettings = () => {
    setFadeOut(true);
    setTimeout(() => {
      if (onOpenSettings) onOpenSettings();
    }, 200); // Match animation duration
  };

  // Don't render anything if we shouldn't show the modal
  if (!isOpen || !shouldShow) return null;
  
  return (
    <div className={`api-key-modal-overlay ${fadeOut ? 'fade-out' : ''}`}>
      <div className="api-key-modal">
        {isChecking ? (
          <div className="api-key-modal-loading">
            <Spinner size="medium" color="#0078ff" label="Checking subscription status..." />
          </div>
        ) : (
          <>
            <h2>Time To Get Your API Key</h2>
            <p>Your free trial has expired - hope you enjoyed it! To continue using Blue Comet, please add your Anthropic API key in Settings.</p>
            
            <div className="api-key-info">
              <p style={{ margin: 0 }}>
                You can get an API key from <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer">Anthropic's Console</a>. It only takes a minute to set up!
              </p>
            </div>
            
            <p style={{ fontSize: '14px', color: '#718096' }}>
              With your own API key, you'll have full access to Claude's capabilities with higher rate limits.
            </p>
            
            <div className="api-key-modal-buttons">
              <button className="api-key-modal-cancel" onClick={handleClose}>
                Cancel
              </button>
              <button className="api-key-modal-settings" onClick={handleOpenSettings}>
                Open Settings
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Add this CSS to the existing CSS file for the fade out animation
const extraStyles = `
@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

.api-key-modal-overlay.fade-out {
  animation: fadeOut 0.2s ease-out forwards;
}

.api-key-modal-overlay.fade-out .api-key-modal {
  animation: slideOut 0.2s ease-out forwards;
}

@keyframes slideOut {
  from { transform: translateY(0); opacity: 1; }
  to { transform: translateY(20px); opacity: 0; }
}
`;

export default ApiKeyRequiredModal;